import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

// Admin-managed image adverts rendered as framed "photo cards" on a warm banner, auto-scrolling.
export const ImageAdSlider = () => {
  const [ads, setAds] = useState([]);
  const rowRef = useRef(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get("/image-ads").then((r) => setAds(r.data || [])).catch(() => {});
  }, []);

  // Auto-scroll one card at a time, looping back to start.
  useEffect(() => {
    if (ads.length <= 1) return undefined;
    const id = setInterval(() => {
      setActive((prev) => {
        const next = (prev + 1) % ads.length;
        const row = rowRef.current;
        if (row) {
          const card = row.children[next];
          if (card) row.scrollTo({ left: card.offsetLeft - row.offsetLeft, behavior: "smooth" });
        }
        return next;
      });
    }, 3500);
    return () => clearInterval(id);
  }, [ads.length]);

  if (ads.length === 0) return null;

  const Card = ({ ad, i }) => {
    const inner = (
      <div className={`relative rounded-2xl bg-white p-2 shadow-lg ring-1 transition-all duration-300 ${i === active ? "ring-amber-400 ring-2 -translate-y-0.5" : "ring-black/5"}`}>
        <div className="aspect-[4/5] rounded-xl overflow-hidden bg-muted">
          <img src={ad.image_url} alt={ad.title || "Advert"} className="w-full h-full object-cover" />
        </div>
        <div className="px-1 pt-2 pb-1">
          {ad.title
            ? <p className="text-primary font-display font-black text-lg sm:text-xl leading-tight truncate">{ad.title}</p>
            : <p className="text-primary font-display font-black text-lg">Shop now</p>}
          <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider text-white bg-red-500 rounded px-2 py-0.5">Sponsored</span>
        </div>
      </div>
    );
    return ad.link_url
      ? <a href={ad.link_url} data-testid={`image-ad-link-${ad.image_ad_id}`} className="block">{inner}</a>
      : inner;
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="image-ad-slider">
      <div className="relative rounded-3xl overflow-hidden shadow-xl p-4 sm:p-6"
        style={{ background: "linear-gradient(135deg,#ff7a18 0%,#e8590c 55%,#c2410c 100%)" }}>
        <div className="absolute inset-0 opacity-[0.12] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 10%, #fff 1.5px, transparent 1.5px)", backgroundSize: "26px 26px" }} />
        <div ref={rowRef} className="relative flex gap-4 overflow-x-auto snap-x scroll-smooth pb-1" style={{ scrollbarWidth: "none" }}>
          {ads.map((ad, i) => (
            <div key={ad.image_ad_id} className="shrink-0 w-[46%] sm:w-[31%] lg:w-[23%] snap-start" data-testid={`image-ad-card-${i}`}>
              <Card ad={ad} i={i} />
            </div>
          ))}
        </div>
        {ads.length > 1 && (
          <div className="relative flex justify-center gap-2 mt-4">
            {ads.map((a, i) => (
              <button key={a.image_ad_id} type="button" data-testid={`image-ad-dot-${i}`} aria-label={`Advert ${i + 1}`}
                onClick={() => {
                  setActive(i);
                  const row = rowRef.current; const card = row?.children[i];
                  if (row && card) row.scrollTo({ left: card.offsetLeft - row.offsetLeft, behavior: "smooth" });
                }}
                className={`h-2 rounded-full transition-all ${i === active ? "w-6 bg-white" : "w-2 bg-white/50 hover:bg-white/80"}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
